import { Injectable } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

export interface StatusBarConfig {
	backgroundColor: string;
	style: Style;
}

/**
 * StatusBar Service
 * 
 * Manages native StatusBar appearance across the app.
 * 
 * Usage Pattern:
 * 1. Inject this service in page components
 * 2. Call appropriate theme method in ionViewWillEnter/ionViewDidEnter
 * 3. Subscribe to theme changes if page headers change color in dark mode
 * 
 * Example:
 * ```typescript
 * ionViewDidEnter() {
 *   if (this.isDarkMode) {
 *     this.statusBarService.setDarkTheme();
 *   } else {
 *     this.statusBarService.setPrimaryTheme();
 *   }
 * }
 * ```
 * 
 * Theme Methods:
 * - setPrimaryTheme(): Teal (#0cb2ac) with white text - for main app views
 * - setDarkTheme(): Dark (#1a1a1a) with white text - for dark mode
 * - setLightTheme(): White with dark text - for light backgrounds
 * - setCustomColor(): Custom color for special cases
 */
@Injectable({
	providedIn: 'root',
})
export class StatusBarService {
	private currentConfig: StatusBarConfig | null = null;

	constructor() {}

	/**
	 * Initialize StatusBar
	 * Note: setOverlaysWebView may not be available on all platforms
	 * The overlay setting is configured in capacitor.config.json instead
	 */
	async initialize(): Promise<void> {
		if (!Capacitor.isNativePlatform()) {
			return;
		}

		// No initialization needed - overlay mode is set in capacitor.config.json
		// Some platforms don't support runtime overlay changes
		console.log('StatusBar initialized via capacitor.config.json');
	}

	/**
	 * Update StatusBar appearance.
	 *
	 * iOS: overlaysWebView is true (default), so the toolbar extends behind the
	 * status bar and its color shows through automatically. Only setStyle() is
	 * called to control text/icon color; setBackgroundColor() is UNIMPLEMENTED.
	 *
	 * Android: Both setStyle() and setBackgroundColor() are supported at runtime.
	 *
	 * @param backgroundColor - Hex color for status bar background (Android only)
	 * @param style - 'Light' for white text/icons, 'Dark' for dark text/icons
	 */
	async setAppearance(backgroundColor: string, style: Style = Style.Light): Promise<void> {
		if (!Capacitor.isNativePlatform()) {
			return;
		}

		// Skip if same config
		if (this.currentConfig?.backgroundColor === backgroundColor && this.currentConfig?.style === style) {
			return;
		}

		const isIos = Capacitor.getPlatform() === 'ios';

		try {
			try {
				await StatusBar.setStyle({ style });
			} catch (styleError: any) {
				if (styleError?.code !== 'UNIMPLEMENTED') {
					console.warn('StatusBar style not available:', styleError?.message || styleError);
				}
			}

			// setBackgroundColor is Android-only; on iOS the overlay shows the toolbar color
			if (!isIos) {
				try {
					await StatusBar.setBackgroundColor({ color: backgroundColor });
				} catch (bgError: any) {
					if (bgError?.code !== 'UNIMPLEMENTED') {
						console.warn('StatusBar background color not available:', bgError?.message || bgError);
					}
				}
			}

			this.currentConfig = { backgroundColor, style };
		} catch (error) {
			console.error('StatusBar appearance update failed:', error);
		}
	}

	/**
	 * Set StatusBar to match primary color (for main app views)
	 */
	async setPrimaryTheme(): Promise<void> {
		await this.setAppearance('#0cb2ac', Style.Light); // Teal with white text
	}

	/**
	 * Set StatusBar to match dark theme (for dark mode or specific views).
	 * Style.Light = white text/icons (counterintuitive but correct for dark backgrounds).
	 */
	async setDarkTheme(): Promise<void> {
		await this.setAppearance('#1a1a1a', Style.Light);
	}

	/**
	 * Set StatusBar to match light theme (for white/light backgrounds)
	 */
	async setLightTheme(): Promise<void> {
		await this.setAppearance('#ffffff', Style.Dark); // White with dark text
	}

	/**
	 * Set custom StatusBar color
	 */
	async setCustomColor(color: string, style: Style = Style.Light): Promise<void> {
		await this.setAppearance(color, style);
	}

	/**
	 * Hide the StatusBar
	 */
	async hide(): Promise<void> {
		if (!Capacitor.isNativePlatform()) {
			return;
		}

		try {
			await StatusBar.hide();
		} catch (error) {
			console.error('StatusBar hide failed:', error);
		}
	}

	/**
	 * Show the StatusBar
	 */
	async show(): Promise<void> {
		if (!Capacitor.isNativePlatform()) {
			return;
		}

		try {
			await StatusBar.show();
		} catch (error) {
			console.error('StatusBar show failed:', error);
		}
	}
}
